import { Listbox, ListboxItem, TmdxAvatar } from '@ds';
import {
  getDailyPublishBlockerMessage,
  getDailySlotPublishBlockerMessage,
  getResourcePublishBlockerMessage,
} from '@lp/features/Scheduler/validation.utils';
import {
  BaseResource,
  BaseShift,
  CriticalRuleViolationType,
  ResourcesWithShifts,
  SchedulePublishReadiness,
} from '@/api/apis/resources/schedule';
import { getFullName } from '@/utils/helpers';

type PublishBlockerTooltipProps<R extends BaseResource, S extends BaseShift<string>> = {
  shiftsData?: ResourcesWithShifts<R, S>;
  publishReadinessData?: SchedulePublishReadiness;
  onResourceClick?: (resourceId: string) => void;
};

export const PublishBlockerTooltip = <R extends BaseResource, S extends BaseShift<string>>({
  shiftsData,
  publishReadinessData,
  onResourceClick,
}: PublishBlockerTooltipProps<R, S>) => {
  const resourceBlocker = publishReadinessData?.blockers?.find(
    (b) => b.type === CriticalRuleViolationType.CriticalResourceRuleViolation,
  );
  const resourceIds = resourceBlocker?.data.ResourcesWithViolation;
  const resourceRuleName = resourceBlocker?.data.CriticalViolatedRuleName;

  if (shiftsData && resourceRuleName && resourceIds?.length) {
    return (
      <Listbox label="Partially filled resources list" topContent="Blocking users">
        {resourceIds.map((resourceId) => {
          const resource = shiftsData[resourceId]?.resource;
          const fullName = getFullName(resource?.firstName, resource?.lastName);

          return (
            <ListboxItem key={resourceId} textValue={fullName} onPress={() => onResourceClick?.(resourceId)}>
              <div className="flex gap-2 items-center">
                <TmdxAvatar radius="lg" size="lg" src={resource?.profilePhoto} name={fullName} />
                <div className="flex flex-col">
                  <span className="text-base-5 text-m-m truncate">{fullName}</span>
                  <span className="text-base-30 text-m-s">{getResourcePublishBlockerMessage(resourceRuleName)}</span>
                </div>
              </div>
            </ListboxItem>
          );
        })}
      </Listbox>
    );
  }

  const dailyBlocker = publishReadinessData?.blockers?.find(
    (b) => b.type === CriticalRuleViolationType.CriticalDailyRuleViolation,
  );
  const partialDays = dailyBlocker?.data.DaysWithViolation;
  const dailyRuleName = dailyBlocker?.data.CriticalViolatedRuleName;

  if (partialDays?.length && dailyRuleName) {
    return (
      <div className="flex flex-col gap-2.5 items-center px-2 py-1">
        {partialDays.map((day) => (
          <span key={day} className="text-base-5 text-m-m">
            {getDailyPublishBlockerMessage(dailyRuleName, day)}
          </span>
        ))}
      </div>
    );
  }

  const dailySlotBlocker = publishReadinessData?.blockers?.find(
    (b) => b.type === CriticalRuleViolationType.CriticalDailySlotRuleViolation,
  );
  const partialDaySlots = dailySlotBlocker?.data.DaySlotsWithViolation;
  const daySlotRuleName = dailySlotBlocker?.data.CriticalViolatedRuleName;

  if (partialDaySlots?.length && daySlotRuleName) {
    return (
      <div className="flex flex-col gap-2.5 items-center px-2 py-1">
        {partialDaySlots.map((daySlotData) => (
          <span key={`${daySlotData.Day}-${daySlotData.Slot}`} className="text-base-5 text-m-m">
            {getDailySlotPublishBlockerMessage(daySlotRuleName, daySlotData)}
          </span>
        ))}
      </div>
    );
  }

  if (publishReadinessData?.blockers?.length) {
    return (
      <div className="flex flex-col gap-2.5 items-center px-1.5 py-1">
        {publishReadinessData.blockers.map((blocker) => (
          <span key={blocker.message} className="text-base-5 text-m-m">
            {blocker.message}
          </span>
        ))}
      </div>
    );
  }

  return null;
};
